import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take } from 'rxjs/operators';
import { DemoModeService } from '../services/demo-mode.service';
import { MembershipService } from '../services/membership.service';
import { TemporalContextService } from '../services/temporal-context.service';
import { resolveStanding } from './standing.util';

/**
 * Blocks routes that are not part of the "Time travel" past view (temporal-ui
 * mode's content-only fence) — the route-level dual of the sidebar's
 * temporal dimming.
 *
 * In-mode navigation to an excluded surface lands on the boundary interstitial
 * (never a silent param strip) with `asOf` preserved so
 * the mode — and its banner — persist, plus `from` so the interstitial can offer
 * the attempted destination in now-mode and pick its workflow copy variant.
 *
 * "In-mode" takes TWO reads, because the router runs every guard before the shell
 * moves the clock: the mode is held now, AND it is still held when this navigation
 * opens its page. The clock alone is the clock of the page being LEFT — a
 * navigation that leaves the mode (the boundary's own "Back to today", a
 * notification or a bookmark, a Back past the mode's entry) still finds the old
 * instant set and was sent to the boundary; from the boundary itself that is the
 * address already shown, so the click did nothing. The mode service answers the
 * second read for the navigation itself.
 *
 * An address that ARRIVES carrying an instant — typed, or kept from an old tab —
 * finds no clock yet, and the page it names would open with the mode beginning
 * under it: today's Selects under the "as it was on …" banner. It meets the same
 * boundary a click inside the mode does. But whether the mode begins at all depends
 * on who is arriving, and on a cold load that is not known when the guard starts: a
 * public brand's signed-in visitor, who never travels, reads as a member until
 * their membership is read (the guards of one route start together, so the sign-in
 * guard beside this one has not waited for it yet), and the boundary is a page the
 * public view does not have. So for an arrival the guard waits for that read, the
 * way the member area's own match does, and asks again; a read that failed leaves
 * the arrival to pass, as it did before the fence reached arrivals. Someone already
 * travelling was admitted long ago and is answered at once.
 *
 * No parameter → plain CanActivateFn (the permission guard is a factory because it
 * carries the permission it asks for; there is nothing to parameterize here).
 */
export const temporalGuard: CanActivateFn = (_route, state) => {
  const temporal = inject(TemporalContextService);
  const router = inject(Router);

  const trigger = router.currentNavigation()?.trigger === 'popstate' ? 'popstate' : 'imperative';
  const toBoundary = (instant: string) => router.createUrlTree(['/time-travel-boundary'], {
    queryParams: { asOf: instant, from: state.url.split('?')[0] },
  });

  const instant = temporal.instantAtActivation(state.url, trigger);
  if (instant === null) {
    return true;
  }
  if (temporal.active()) {
    return toBoundary(instant);
  }

  // An arrival. Demo mode has no sign-in state and no visitor; an anonymous caller is known for what they are
  // without a membership read; a signed-in one is known once theirs has landed.
  if (inject(DemoModeService).isDemo()) {
    return toBoundary(instant);
  }
  const membership = inject(MembershipService);
  return inject(OidcSecurityService).isAuthenticated$.pipe(
    take(1),
    switchMap(async ({ isAuthenticated }) => {
      // A read that failed says nothing about who is arriving, and the sign-in guard beside this one may read again
      // and admit a visitor: the arrival passes, as every guard keeps its earlier answer on an unknown standing.
      if (isAuthenticated && (await resolveStanding(membership)) === 'unknown') return true;
      const settled = temporal.instantAtActivation(state.url, trigger);
      return settled === null ? true : toBoundary(settled);
    }),
  );
};
